import {
	CardContent,
	DialogProps,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import {
	DataGridButton,
	DataGridButtonType,
	DropDownButton,
	getSubscriptions,
	rem,
	Text,
	useIsFeatureEnabled,
	WarningDialog,
	useStyles,
} from '@innovyze/stylovyze';
import { NewModelCardWrapper } from './RiskListCard.style';
import { fullAccess } from '@innovyze/shared-utils';
import NewRiskIcon from '@Components/Utils/icons/NewRisk';
import RiskListCard from './RiskListCard.component';
import { useGlobalization } from '@Translations/useGlobalization';
import { FullPage } from '@Utils/styles';
import {
	selectRiskConfigsIsLoading,
	selectRiskConfigsIsWaiting,
	selectRiskConfigsPage,
	selectRiskConfigsPagination,
	selectRiskConfigsPerPage,
} from '@innovyze/lib-am-common/Selectors';
import { RiskConfigCard } from '@innovyze/lib-am-common';
import { NewRisk } from '@Components/NewRisk';
import {
	selectRefreshList,
	selectRiskConfigIsCreating,
	selectRiskIsDeleting,
} from '@Selectors/Risk.selectors';
import { useDispatch } from 'react-redux';
import { deleteRisk, setRefreshList, setRiskType } from '@Actions/Risk.actions';
import {
	changeRiskConfigsPage,
	changeRiskConfigsSlice,
	changeRiskConfigsSort,
	getAssetCounts,
} from '@innovyze/lib-am-common/Actions';
import { FlexStyledGrid, StyledPagination, StyledText } from './RiskList.style';
import { ConfigPublishState } from '@Types/riskConfig.types';
import { useSystemTypes } from '@Hooks/useSystemTypes';

const RiskList = () => {
	const { t } = useGlobalization();
	const { themePalette } = useStyles();
	const dispatch = useDispatch();

	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);
	const enableAddNew = useIsFeatureEnabled(
		'info-360-asset-multiple-risk-models-create',
	);
	const riskPage = selectRiskConfigsPage();
	const pagination = selectRiskConfigsPagination();
	const isLoading = selectRiskConfigsIsLoading();
	const isWaiting = selectRiskConfigsIsWaiting();
	const isCreating = selectRiskConfigIsCreating();
	const isDeleting = selectRiskIsDeleting();
	const [configId, setConfigId] = useState('');
	const [openDelete, setOpenDelete] = useState(false);
	const [duplicateRiskModel, setDuplicateRiskModel] = useState<
		RiskConfigCard | undefined
	>(undefined);
	const [isBlockDeleteDialogOpen, setIsBlockDeleteDialogOpen] =
		useState(false);
	const [newModel, setNewModel] = useState(false);
	const refreshList = selectRefreshList();

	const dispatchSliceFn = (slice: number) =>
		dispatch(changeRiskConfigsSlice(slice));
	const rowsPerPage = selectRiskConfigsPerPage(dispatchSliceFn);

	const systemTypes = useSystemTypes();

	const sortOptions = [
		{ id: 'createdAt-D', caption: t('Date Created/Modified - Descending') },
		{ id: 'createdAt-A', caption: t('Date Created/Modified - Ascending') },
		{ id: 'name-D', caption: t('Name - Descending') },
		{ id: 'name-A', caption: t('Name - Ascending') },
		{ id: 'lastRun-D', caption: t('Last Run - Descending') },
		{ id: 'lastRun-A', caption: t('Last Run - Ascending') },
		{ id: 'runBy-D', caption: t('Run By - Descending') },
		{ id: 'runBy-A', caption: t('Run By - Ascending') },
	];
	const [selectedSort, setSelectedSort] = useState('createdAt-D');

	const options = sortOptions.map(idCaption => {
		return (
			<MenuItem value={idCaption.id} key={idCaption.id}>
				{idCaption.caption}
			</MenuItem>
		);
	});

	const onRefresh = () => {
		dispatch(changeRiskConfigsPage(riskPage.page));
		dispatch(getAssetCounts(systemTypes.map(s => s.id)));
	};

	useEffect(() => {
		if (refreshList) {
			onRefresh();
			dispatch(setRefreshList(false));
		}
		if (pagination && pagination.sortKey) {
			setSelectedSort(
				`${pagination.sortKey}-${
					pagination.sortDescending ? 'D' : 'A'
				}`,
			);
		}
	}, []);

	const handleDuplicateRisk = (riskConfig: RiskConfigCard) => {
		setDuplicateRiskModel(riskConfig);
		setNewModel(true);
	};

	const handleDeleteRisk = (id: string, publishState: ConfigPublishState) => {
		if (publishState === ConfigPublishState.unpublished) {
			setConfigId(id);
			setOpenDelete(true);
		} else {
			setIsBlockDeleteDialogOpen(true);
		}
	};

	const handleDeleteConfirmYes = () => {
		dispatch(
			deleteRisk({
				configId: configId,
				refresh: onRefresh,
			}),
		);
		setOpenDelete(false);
	};

	const handleDeleteConfirmNo = () => {
		setOpenDelete(false);
	};

	const onClickOpenCreateNewModel = () => {
		if (!(isLoading || isWaiting || isDeleting || isCreating)) {
			dispatch(setRiskType('linear'));
			setNewModel(true);
		}
	};

	const onNewModelClose = () => {
		setNewModel(false);
		setDuplicateRiskModel(undefined);
	};

	const createRiskCard = () => {
		const primaryPalette = themePalette?.primary;
		return (
			<NewModelCardWrapper>
				<CardContent style={{ height: '100%' }}>
					<div
						role="button"
						onClick={() => onClickOpenCreateNewModel()}
						onKeyDown={() => onClickOpenCreateNewModel()}
						tabIndex={0}
						className="model-create_card__icon"
						data-cy="new-model">
						<NewRiskIcon
							mainColor={primaryPalette?.main}
							darkColor={primaryPalette?.dark}
						/>
						<div className="model-create_card__note">
							<Typography gutterBottom variant="subtitle1">
								{t('Create New Risk Model')}
							</Typography>
						</div>
					</div>
				</CardContent>
			</NewModelCardWrapper>
		);
	};

	const sortChange = (event: SelectChangeEvent<typeof selectedSort>) => {
		const target = event?.target as HTMLInputElement;
		const sort = target.value.split('-');
		dispatch(
			changeRiskConfigsSort({
				key: sort[0],
				descending: sort[1] === 'D',
			}),
		);
		setSelectedSort(target.value);
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: openDelete,
		onClose: onCloseDialog,
	};

	const renderBlockRiskDeleteDialog = useCallback(() => {
		return (
			<WarningDialog
				open={isBlockDeleteDialogOpen}
				title={t('Risk Model Cannot be Deleted')}
				content={t(
					'You cannot delete a Risk Model that is published. Unpublish and try deleting again.',
				)}
				cancelText={t('Close')}
				onCancel={() => setIsBlockDeleteDialogOpen(false)}
			/>
		);
	}, [isBlockDeleteDialogOpen]);

	return (
		<FullPage
			className={
				isLoading || isWaiting || isDeleting || isCreating ? 'wait' : ''
			}>
			<WarningDialog
				dialogProps={dialogProps}
				data-cy="delete-dialog"
				open={openDelete}
				title={t('Delete Risk?')}
				content={t('Are you sure you want to delete this Risk Model?')}
				cancelText={t('Cancel')}
				cancelButtonProps={{
					disabled: isDeleting,
					dataCy: 'delete-cancel',
				}}
				confirmText={t('Delete')}
				confirmButtonProps={{
					disabled: isDeleting,
					dataCy: 'delete-confirm',
				}}
				onCancel={handleDeleteConfirmNo}
				onConfirm={handleDeleteConfirmYes}
			/>
			{renderBlockRiskDeleteDialog()}
			<NewRisk
				open={newModel}
				duplicate={duplicateRiskModel}
				onClose={onNewModelClose}
			/>
			<Grid container spacing={2} direction="row">
				<Grid item xs={10} sm={6} md={4} lg={3} xl={2}>
					<FormControl fullWidth variant="standard">
						<InputLabel>{t('Sort By')}</InputLabel>
						<Select
							data-cy="sort-by"
							value={selectedSort}
							disabled={
								isLoading ||
								isWaiting ||
								isDeleting ||
								isCreating
							}
							name={t('Sort By')}
							onChange={sortChange}>
							{options}
						</Select>
					</FormControl>
				</Grid>
				<Grid
					item
					xs={2}
					sm={6}
					md={8}
					lg={9}
					xl={10}
					container
					justifyContent="flex-end"
					data-cy="grid-actionbutton">
					<DataGridButton
						type={DataGridButtonType.REFRESH}
						disabled={isWaiting}
						data-cy="refreshButton"
						onClick={onRefresh}
					/>
				</Grid>
				{hasFullAccess && enableAddNew ? (
					<Grid xs={12} sm={6} md={4} lg={3} xl={3} item>
						{createRiskCard()}
					</Grid>
				) : (
					<></>
				)}
				{riskPage?.cards.map((riskModel: RiskConfigCard) => (
					<Grid
						xs={12}
						sm={6}
						md={4}
						lg={3}
						xl={3}
						item
						key={riskModel?.configId}>
						<RiskListCard
							riskConfig={riskModel}
							onDuplicateRisk={handleDuplicateRisk}
							onDeleteRisk={handleDeleteRisk}
						/>
					</Grid>
				))}
				{!isWaiting ? (
					<Grid
						item
						container
						xs={12}
						style={{
							padding: `${rem(16)} ${rem(24)}`,
						}}>
						<Grid item container xs={6}>
							<Stack
								direction={'row'}
								spacing={'1rem'}
								alignItems={'center'}
								justifyContent={'space-between'}>
								<Text variant="text-large">
									{t('Models per page:')}
								</Text>
								<DropDownButton
									arrow
									color="secondary"
									options={rowsPerPage.options}
									placement="bottom-start"
									variant="text"
									initialSelectedIndex={
										rowsPerPage.selectedIndex
									}
									fitButtonToContent
									fitMenuToContent
								/>
							</Stack>
						</Grid>
						<Grid item xs={6}>
							{riskPage.pages > 1 ? (
								<FlexStyledGrid>
									<StyledPagination
										count={pagination.pages}
										page={pagination.page}
										onChange={(
											_e: unknown,
											page: number,
										) => {
											dispatch(
												changeRiskConfigsPage(page),
											);
											dispatch(
												getAssetCounts(
													systemTypes.map(s => s.id),
												),
											);
										}}
										variant="outlined"
										$borderColor={
											themePalette?.primary?.dark
										}
									/>
									<StyledText variant="caption-small">
										{t('{{from}}-{{to}} of {{total}}', {
											from:
												(pagination.page - 1) *
													pagination.slice +
												1,
											to:
												pagination.page ==
												pagination.pages
													? pagination.total
													: pagination.page *
													  pagination.slice,
											total: pagination.total,
										})}
									</StyledText>
								</FlexStyledGrid>
							) : (
								[]
							)}
						</Grid>
					</Grid>
				) : (
					[]
				)}
			</Grid>
		</FullPage>
	);
};
export default RiskList;
